/**
 * ISO 3166-1 alpha-2 Country Code Validator
 * Complete list of valid 2-letter country codes
 */

const VALID_ISO_CODES = new Set([
    'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT',
    'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI',
    'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY',
    'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
    'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM',
    'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK',
    'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL',
    'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
    'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR',
    'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN',
    'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS',
    'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
    'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
    'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP',
    'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM',
    'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
    'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM',
    'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF',
    'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW',
    'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
    'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW'
]);

export interface ValidationResult {
    valid: boolean;
    code: string;
    error: string | null;
}

/**
 * Validate ISO 3166-1 alpha-2 country code
 * @param {string} countryCode - 2-letter country code to validate
 * @returns {ValidationResult} - { valid: boolean, code: string, error: string|null }
 */
export function validateISOCode(countryCode: string): ValidationResult {
    if (!countryCode || countryCode.trim() === '') {
        return {
            valid: false,
            code: '',
            error: 'Enter a valid ISO code'
        };
    }

    const normalized = countryCode.trim().toUpperCase();

    if (normalized.length !== 2) {
        return {
            valid: false,
            code: normalized,
            error: 'Enter a valid ISO code'
        };
    }

    if (!/^[A-Z]{2}$/.test(normalized)) {
        return {
            valid: false,
            code: normalized,
            error: 'Enter a valid ISO code'
        };
    }

    if (!VALID_ISO_CODES.has(normalized)) {
        return {
            valid: false,
            code: normalized,
            error: 'Enter a valid ISO code'
        };
    }

    return {
        valid: true,
        code: normalized,
        error: null
    };
}

/**
 * Check if a country code is valid (simple boolean check)
 * @param {string} countryCode - 2-letter country code
 * @returns {boolean}
 */
export function isValidISOCode(countryCode: string): boolean {
    if (!countryCode) return false;
    const normalized = countryCode.trim().toUpperCase();
    return normalized.length === 2 && /^[A-Z]{2}$/.test(normalized) && VALID_ISO_CODES.has(normalized);
}
